import type { SupabaseClient } from '@supabase/supabase-js';

/** Prefix of every key LingoPing writes to localStorage (see StorageManager and the services). */
const OWN_KEY_PREFIX = 'lingoping_';

/**
 * Removes the browser's copy of a user's data: everything in the IndexedDB store, plus any
 * LingoPing key in localStorage. The Supabase session itself is removed by supabase-js.
 *
 * The dashboard's own sign-out (`signOutUser`) first sends what the learner changed and then
 * removes their cards, known words and review history. This goes further, because on a website
 * another person may sign in next on the same computer: the decks and every other note LingoPing
 * left go too. It also runs when the session ends without the dashboard's sign-out (signed out
 * in another tab, a session that no longer works); changes that had not reached the server by
 * then are gone with it.
 */
export async function clearLocalData(
  store: { clear(): Promise<void> },
  local: Storage | undefined = browserLocalStorage()
): Promise<void> {
  await store.clear();
  if (!local) return;
  const own: string[] = [];
  for (let i = 0; i < local.length; i++) {
    const key = local.key(i);
    if (key?.startsWith(OWN_KEY_PREFIX)) own.push(key);
  }
  own.forEach((key) => local.removeItem(key));
}

function browserLocalStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined; // storage blocked by the browser
  }
}

interface SignOutHooks {
  clear: () => Promise<void>;
  /** Starts the page afresh, so nothing of the old user stays in memory either. */
  reload: () => void;
  /** The development build signs in without a Supabase session, so "no session" is normal there. */
  isSimulatedSignIn: () => boolean;
}

/**
 * Clears the browser's copy of the data when the Supabase session ends (sign-out here, sign-out in
 * another tab, or a refresh token that no longer works), then reloads the page. A page that finds
 * no session at all also clears, in case an earlier sign-out was interrupted. Returns the
 * function that stops listening.
 */
export function clearDataWhenSignedOut(
  auth: SupabaseClient['auth'],
  { clear, reload, isSimulatedSignIn }: SignOutHooks
): () => void {
  const { data } = auth.onAuthStateChange((event, session) => {
    // Not awaited: supabase-js must not be called from inside this callback.
    if (event === 'SIGNED_OUT') {
      void clear()
        .catch((err) => console.error('[LingoPing] Could not clear the saved data:', err))
        .then(reload);
    } else if (event === 'INITIAL_SESSION' && !session && !isSimulatedSignIn()) {
      void clear().catch((err) =>
        console.error('[LingoPing] Could not clear the saved data:', err)
      );
    }
  });
  return () => data.subscription.unsubscribe();
}
